import {
  TableContainer,
  Table,
  TableRow,
  styled,
  TableCell,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  ScheduleMode,
  type BackupVault,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';
import React from 'react';

import { VaultTag } from '@/components/vaults/vaultTag';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import {
  deconstructCron,
  getFrequencyByWindow,
  getFrequencyDisplayName,
  getHoursDisplay,
  getWindowDurationDisplay,
} from '../frequency';

const PolicyTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  padding: 0,
  verticalAlign: 'top',
  paddingRight: '40px',

  '&:last-child': {
    paddingRight: 0,
  },
}));

const PolicyTableHeaderCell = styled(TableCell)(() => ({
  border: 'none',
  padding: 0,
  paddingBottom: '24px',
  paddingRight: '40px',

  '&:last-child': {
    paddingRight: 0,
  },
}));

interface BackupPolicyRetentionSchedulesTableProps {
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
}

export function BackupPolicyRetentionSchedulesTable(
  props: BackupPolicyRetentionSchedulesTableProps
) {
  return (
    <Stack>
      {props.policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && (
        <Typography className='mb-[36px] mt-[12px]'>
          {props.policy.schedules[0]?.useUtc
            ? `Backup schedules are in UTC.`
            : 'Backup schedules follow the resource time zones.'}
        </Typography>
      )}
      <TableContainer className='mb-[12px]'>
        <Table>
          <thead>
            <TableRow>
              <PolicyTableHeaderCell width='272px'>
                <Typography variant='h4'>Frequency</Typography>
              </PolicyTableHeaderCell>
              <PolicyTableHeaderCell width='140px'>
                <Typography variant='h4'>Retention</Typography>
              </PolicyTableHeaderCell>
              <PolicyTableHeaderCell>
                <Typography variant='h4'>Vault</Typography>
              </PolicyTableHeaderCell>
            </TableRow>
          </thead>
          <tbody>
            {props.policy.schedules.map((schedule, index, items) => {
              const vault = props.vaults?.find(
                (v) => v.id === schedule.vaultId
              );
              const formattedRetention = fromDaysToBiggestAsPossible(
                schedule.backupRetention
              );

              const { hours, minutes } = deconstructCron(schedule.windows);
              const frequencyDisplayName = getFrequencyDisplayName(
                schedule.windows
              );

              return (
                <React.Fragment key={schedule.id}>
                  <TableRow>
                    <PolicyTableCell width='272px'>
                      <Stack direction='row' gap='12px'>
                        <Typography
                          width='80px'
                          minWidth='80px'
                          maxWidth='80px'
                        >
                          {getFrequencyByWindow(schedule.windows)?.text}
                        </Typography>
                        {props.policy.scheduleMode ===
                          ScheduleMode.SCHEDULE_MODE_ADVANCED && (
                          <Stack gap='12px' sx={{ maxWidth: '140px' }}>
                            {frequencyDisplayName && (
                              <Typography>{frequencyDisplayName}</Typography>
                            )}
                            <Typography variant='subtitle2'>
                              {getHoursDisplay(hours, minutes)}
                              <br />
                              {`Starts within ${getWindowDurationDisplay(schedule.duration / 60)}`}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </PolicyTableCell>
                    <PolicyTableCell width='140px'>
                      <Stack gap='4px'>
                        <Typography>{`${formattedRetention.value} ${formattedRetention.unit}`}</Typography>
                        {formattedRetention.unit !== 'days' && (
                          <Typography>{`(${schedule.backupRetention} days)`}</Typography>
                        )}
                      </Stack>
                    </PolicyTableCell>
                    <PolicyTableCell>
                      {vault && <VaultTag vault={vault} />}
                    </PolicyTableCell>
                  </TableRow>
                  {index !== items.length - 1 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{ border: 'none', padding: '24px 0' }}
                      >
                        <Divider sx={{ borderStyle: 'dashed' }} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
